import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { merge, Subject, filter, takeUntil } from 'rxjs';
import {
  ClientsService,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import {
  ClientCreditTransferItem,
  CreditsService,
} from 'src/app/services/credits/credits.service';
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardContent,
  MatCardFooter,
} from '@angular/material/card';
import { PayeeMiniIconButtonComponent } from '../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { CurrencyPipe, DatePipe } from '@angular/common';

@Component({
  selector: 'app-credit-transfers',
  templateUrl: './credit-transfers.component.html',
  styleUrls: ['./credit-transfers.component.scss'],
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    MatCardFooter,
    MatPaginator,
    CurrencyPipe,
    DatePipe,
    TranslateModule,
  ],
})
export class CreditTransfersComponent implements OnInit, OnDestroy {
  private creditsService = inject(CreditsService);
  private translate = inject(TranslateService);
  private clientsService = inject(ClientsService);
  private snackbar = inject(MatSnackBar);

  private readonly destroy = new Subject<void>();

  readonly loading = signal(false);
  creditTransfers: ClientCreditTransferItem[] = [];
  pagedCreditTransfers: ClientCreditTransferItem[] = [];
  pageIndex = 0;
  pageSize = 5;

  ngOnInit(): void {
    merge(
      this.clientsService.selectedClientSubject.pipe(
        filter((client): client is SelectedClient => !!client),
      ),
      this.creditsService.refreshTransferHistory,
    )
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.setCreditTransfers());
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  pageChange(event: PageEvent): void {
    this.pagedCreditTransfers = this.creditTransfers.slice(
      event.pageIndex * event.pageSize,
      (event.pageIndex + 1) * event.pageSize,
    );
  }

  async cancelCreditTransfer(
    creditTransfer: ClientCreditTransferItem,
  ): Promise<void> {
    try {
      await this.creditsService.cancelCreditPurchase(creditTransfer.id);
      creditTransfer.state = 'cancelled';
    } catch (error) {
      console.error('Error while deleting credit transfer', error);
      this.snackbar.open(
        this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
    }
  }

  private async setCreditTransfers(): Promise<void> {
    if (!this.clientsService.selectedClientId) {
      return;
    }

    try {
      this.loading.set(true);
      this.creditTransfers = await this.creditsService.getCreditTransfers();
    } catch (error) {
      console.error('Error while getting credit transfers', error);
      this.snackbar.open(
        this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
    } finally {
      this.loading.set(false);
      this.pageChange({
        length: this.creditTransfers.length,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
      });
    }
  }
}
